import {
    createSlice,
    createEntityAdapter,
    EntityState,
    PayloadAction,
    createAsyncThunk,
    Update
} from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from '../index';
import {
    collection, query, getDocs, QueryConstraint,
    addDoc, updateDoc, deleteDoc, doc, Timestamp, onSnapshot, Unsubscribe,
    DocumentChange, GeoPoint
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
// Adjust import path as needed
import type { FirestoreLocation } from '@/types/firestore';

// --- Serialization Helper ---
// Handles Timestamp and GeoPoint, can be expanded for other Firestore types
const serializeFirestoreData = (data: any): any => {
    if (data === null || typeof data !== 'object') {
        return data;
    }

    if (data instanceof Timestamp) {
        return data.toMillis(); // Or .toDate().toISOString() if you prefer ISO strings
    }

    if (data instanceof GeoPoint) {
        return {
            latitude: data.latitude,
            longitude: data.longitude,
        };
    }

    if (Array.isArray(data)) {
        return data.map(serializeFirestoreData);
    }

    // For plain objects, process each value
    const serializedObject: { [key: string]: any } = {};
    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            serializedObject[key] = serializeFirestoreData(data[key]);
        }
    }
    return serializedObject;
};

// Define entity adapter
// Explicitly type the adapter with <FirestoreLocation, string>
// Provide selectId to handle 'id?: string' and conform to string ID type
const locationsAdapter = createEntityAdapter<FirestoreLocation, string>({
    selectId: (location) => location.id as string, // Assume ID is always present in the state
});

// Define the state shape
// Specify both the entity type (FirestoreLocation) and its ID type (string)
interface LocationsState extends EntityState<FirestoreLocation, string> {
    loading: 'idle' | 'pending' | 'succeeded' | 'failed';
    error: string | null;
    listenerActive: boolean; // Flag to track if the listener is active
    // Potentially add metadata about the fetched locations, like last query options
    // lastQueryOptions?: any;
}

// Define the initial state using the adapter
const initialState: LocationsState = locationsAdapter.getInitialState({
    loading: 'idle',
    error: null,
    listenerActive: false, // Initialize listener flag
});

// --- Listener Management ---
let locationsUnsubscribe: Unsubscribe | null = null;

// --- Async Thunks ---

// Thunk to fetch locations based on query constraints
// Note: This doesn't set up listeners, it's a one-time fetch.
// Real-time updates might be handled differently (e.g., via listeners in map components or a dedicated service).
export const fetchLocations = createAsyncThunk<
    FirestoreLocation[], // Return type on success
    QueryConstraint[] | undefined, // Argument type (optional query constraints)
    { rejectValue: string } // Thunk config
>(
    'locations/fetchLocations',
    async (queryConstraints, { rejectWithValue }) => {
        try {
            const locationsColRef = collection(db, 'locations');
            const q = queryConstraints ? query(locationsColRef, ...queryConstraints) : query(locationsColRef);
            const querySnapshot = await getDocs(q);
            const locations = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FirestoreLocation));
            return locations;
        } catch (error: any) {
            console.error('[LocationsSlice] Error fetching locations:', error);
            return rejectWithValue(error.message || 'Failed to fetch locations');
        }
    }
);

// Thunk to initialize the Firestore listener for locations
export const initializeLocationsListener = createAsyncThunk<
    void,
    void,
    { dispatch: AppDispatch; rejectValue: string; state: RootState }
>(
    'locations/initializeListener',
    async (_, { dispatch, rejectWithValue, getState }) => {
        if (locationsUnsubscribe) {
            return;
        }
        // Prevent re-initialization if already active according to state
        if (getState().locations.listenerActive) {
             return;
        }

        dispatch(setLoading('pending'));

        try {
            const q = collection(db, 'locations');
            locationsUnsubscribe = onSnapshot(q,
                (snapshot) => {
                    const changes = snapshot.docChanges();

                    const added: FirestoreLocation[] = [];
                    const modified: Update<FirestoreLocation, string>[] = [];
                    const removed: string[] = [];

                    changes.forEach((change: DocumentChange) => {
                        // Serialize the document data here
                        const rawData = change.doc.data();
                        if (!rawData) return; // Should not happen if doc exists

                        const serializedData = serializeFirestoreData(rawData);
                        const id = change.doc.id;

                        if (change.type === "added") {
                            // Ensure ID is included for added items
                            added.push({ id, ...serializedData } as FirestoreLocation);
                        }
                        if (change.type === "modified") {
                            // For updateMany, payload needs to be { id: string, changes: Partial<T> }
                            // Add debugging to see what's modified
                            
                            // Include the full serialized data and ID in the changes
                            // This ensures Redux creates a new object reference when updating the state
                            // We need to include a full new object to ensure the caching in mapSelectors
                            // properly detects the change
                            modified.push({ 
                                id: id, 
                                changes: { 
                                    ...serializedData,
                                    id // Ensure ID is included in the changes object 
                                } 
                            }); 
                        }
                        if (change.type === "removed") {
                            removed.push(id);
                        }
                    });

                    // Dispatch actions with payloads
                    if (added.length > 0) dispatch(upsertLocations(added));
                    if (modified.length > 0) dispatch(updateLocations(modified));
                    if (removed.length > 0) dispatch(removeLocationsById(removed));

                    // Mark loading as succeeded only after first snapshot or if state indicates listener is now active
                     if (getState().locations.loading === 'pending' || !getState().locations.listenerActive) {
                        dispatch(setListenerInitialized(true)); // Mark listener as active and set loading state
                    }
                },
                (error) => {
                    console.error('[LocationsSlice] Listener error:', error);
                    dispatch(setError(`Listener error: ${error.message}`));
                    dispatch(setListenerInitialized(false)); // Mark listener as inactive on error
                    cleanupLocationsListener(); // Cleanup on error
                    // Reject the thunk promise
                    rejectWithValue(`Listener error: ${error.message}`);
                }
            );
             // Initial state is handled by the first snapshot callback
             // Or we could dispatch setListenerInitialized(true) here? Let's keep it in the callback for now.

        } catch (error: any) {
            console.error("[LocationsSlice] Error setting up listener:", error);
            dispatch(setError(`Setup error: ${error.message}`));
            dispatch(setListenerInitialized(false));
            locationsUnsubscribe = null; // Ensure it's null on setup error
            return rejectWithValue(`Setup error: ${error.message}`);
        }
    }
);

// Function to cleanup the listener
export const cleanupLocationsListener = () => {
    if (locationsUnsubscribe) {
        locationsUnsubscribe();
        locationsUnsubscribe = null;
        // Optionally dispatch an action to set listenerActive to false and reset state
        // store.dispatch(setListenerInitialized(false)); // Requires access to store or dispatch
    } else {
    }
};


// Thunk to add a new location (example, might move to a more specific slice/hook later)
export const addLocation = createAsyncThunk<
    FirestoreLocation, // Return the created location with ID
    Omit<FirestoreLocation, 'id' | 'createdAt' | 'updatedAt'>, // Argument type (data without generated fields)
    { rejectValue: string }
>(
    'locations/addLocation',
    async (locationData, { rejectWithValue }) => {
        try {
            // Ensure locationData is serializable before sending to Firestore if it comes from UI
            // For coordinates, Firestore expects a GeoPoint, so if your input is already {lat, lng}, convert it.
            // However, this thunk's input `locationData` is Omit<FirestoreLocation, ...>
            // and FirestoreLocation might still define coordinates as GeoPoint or {lat,lng}.
            // For this example, assuming `locationData.coordinates` is ALREADY in the format
            // Firestore expects or is serializable. If it's a GeoPoint from elsewhere, it's an issue.
            // The critical part is what's returned and put into the store via listener.
            
            // Create a GeoPoint for Firestore if coordinates are {latitude, longitude}
            let processedCoordinates = locationData.coordinates;
            if (locationData.coordinates && 
                typeof locationData.coordinates === 'object' &&
                'latitude' in locationData.coordinates && 
                'longitude' in locationData.coordinates &&
                !(locationData.coordinates instanceof GeoPoint) // Avoid re-wrapping
            ) {
                processedCoordinates = new GeoPoint(
                    (locationData.coordinates as { latitude: number; longitude: number }).latitude,
                    (locationData.coordinates as { latitude: number; longitude: number }).longitude
                );
            }


            const dataToSend = {
                ...locationData,
                coordinates: processedCoordinates, // Use potentially converted GeoPoint
                createdAt: Timestamp.now(), // Firestore Timestamp
                updatedAt: Timestamp.now(), // Firestore Timestamp
            };
            const docRef = await addDoc(collection(db, 'locations'), dataToSend);
            
            // Data returned here should be serializable if it were to bypass the listener.
            // However, the listener is the primary source of truth for store updates.
            // The listener will serialize what it gets from Firestore.
            return { 
                id: docRef.id, 
                ...locationData, // Original data structure
                coordinates: locationData.coordinates, // Return original serializable structure for coordinates
                createdAt: dataToSend.createdAt.toMillis(), // Serialize timestamp
                updatedAt: dataToSend.updatedAt.toMillis()  // Serialize timestamp
            } as unknown as FirestoreLocation;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to add location');
        }
    }
);

// Thunk to update a location
export const updateLocation = createAsyncThunk<
    { id: string; changes: Partial<FirestoreLocation> }, // Return id and changes on success
    { id: string; data: Partial<Omit<FirestoreLocation, 'id' | 'createdAt'>> }, // Argument: id and data
    { rejectValue: string }
>(
    'locations/updateLocation',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const docRef = doc(db, 'locations', id);
            
            // Handle coordinates if present in `data`
            let processedCoordinates = data.coordinates;
            if (data.coordinates && 
                typeof data.coordinates === 'object' &&
                'latitude' in data.coordinates && 
                'longitude' in data.coordinates &&
                !(data.coordinates instanceof GeoPoint)
            ) {
                 processedCoordinates = new GeoPoint(
                    (data.coordinates as { latitude: number; longitude: number }).latitude,
                    (data.coordinates as { latitude: number; longitude: number }).longitude
                );
            }

            const dataToSend = {
                ...data,
                ...(processedCoordinates && { coordinates: processedCoordinates }), // Include if processed
                updatedAt: Timestamp.now(),
            };
            await updateDoc(docRef, dataToSend);
            // Listener should pick this up.
            // The return value should be serializable if used directly.
            return { 
                id, 
                changes: {
                    ...data, // original data changes
                    updatedAt: (dataToSend.updatedAt as Timestamp).toMillis() // ensure serializable
                } as unknown as Partial<FirestoreLocation> 
            };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update location');
        }
    }
);

// Thunk to delete a location (Note: Cascading deletes handled by Cloud Function)
export const deleteLocation = createAsyncThunk<
    string, // Return the ID of the deleted location
    string, // Argument: id
    { rejectValue: string }
>(
    'locations/deleteLocation',
    async (id, { rejectWithValue }) => {
        try {
            const docRef = doc(db, 'locations', id);
            await deleteDoc(docRef);
            // Listener should pick this up, no need to manually remove from state
            // Cloud Function 'recursiveDelete' should handle subcollections
            return id; // Return the deleted ID
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete location');
        }
    }
);

// --- Slice Definition ---
const locationsSlice = createSlice({
    name: 'locations',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<'idle' | 'pending'>) => {
            // Prevent listener loading state from being overridden by other actions
            if (state.loading !== 'pending' || !state.listenerActive) {
                 state.loading = action.payload;
            }
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.loading = 'failed';
            state.error = action.payload;
             state.listenerActive = false; // Assume listener failed if error is set externally
             cleanupLocationsListener(); // Clean up if an external error occurs
        },
        // Reducer to handle listener initialization status and loading state
        setListenerInitialized: (state, action: PayloadAction<boolean>) => {
            state.listenerActive = action.payload;
            state.loading = action.payload ? 'succeeded' : 'idle'; // Set loading based on status
            if (!action.payload) {
                state.error = state.error ?? 'Listener stopped.'; // Set error if stopped without explicit error
            } else {
                 state.error = null; // Clear error on successful init
            }
        },
        // Reducers for batch updates from listener
        upsertLocations: (state, action: PayloadAction<FirestoreLocation[]>) => {
            locationsAdapter.upsertMany(state, action.payload);
        },
        updateLocations: (state, action: PayloadAction<Update<FirestoreLocation, string>[]>) => {
            // Add detailed logging to see what properties are being updated
            locationsAdapter.updateMany(state, action.payload);
        },
        removeLocationsById: (state, action: PayloadAction<string[]>) => {
            locationsAdapter.removeMany(state, action.payload);
        },
        // Optionally clear locations or reset state
        clearLocations: (state) => {
            locationsAdapter.removeAll(state);
            state.loading = 'idle';
            state.error = null;
            state.listenerActive = false; // Ensure listener flag is reset
            cleanupLocationsListener(); // Clean up listener when clearing data
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchLocations (still useful for initial load before listener or specific queries)
            .addCase(fetchLocations.pending, (state) => {
                // Only set pending if listener isn't active or already loading
                if (!state.listenerActive && state.loading !== 'pending') {
                     state.loading = 'pending';
                }
                state.error = null;
            })
            .addCase(fetchLocations.fulfilled, (state, action) => {
                 // Only update loading state if listener isn't active
                 if (!state.listenerActive) {
                     state.loading = 'succeeded';
                 }
                // Use setAll to replace existing locations with fetched ones
                locationsAdapter.setAll(state, action.payload);
            })
            .addCase(fetchLocations.rejected, (state, action) => {
                // Only update loading state if listener isn't active
                 if (!state.listenerActive) {
                    state.loading = 'failed';
                 }
                state.error = action.payload ?? 'Unknown error fetching';
            })
            // initializeLocationsListener
            .addCase(initializeLocationsListener.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
                state.listenerActive = false; // Mark as inactive until first snapshot confirms
            })
            .addCase(initializeLocationsListener.fulfilled, () => {
                // State (loading, listenerActive) is managed by the listener callback via setListenerInitialized
            })
            .addCase(initializeLocationsListener.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.payload ?? 'Listener initialization failed';
                state.listenerActive = false;
                locationsUnsubscribe = null; // Ensure unsubscribe is null on rejection
            })
            // addLocation (No state change needed, listener handles it)
            .addCase(addLocation.rejected, (state, action) => {
                state.error = action.payload ?? 'Failed to add location';
            })
            // updateLocation (No state change needed, listener handles it)
             .addCase(updateLocation.rejected, (state, action) => {
                state.error = action.payload ?? 'Failed to update location';
            })
            // deleteLocation (No state change needed, listener handles it)
             .addCase(deleteLocation.rejected, (state, action) => {
                state.error = action.payload ?? 'Failed to delete location';
            });
    },
});

// Export actions and reducer
export const { 
    setLoading, 
    setError, 
    clearLocations, 
    setListenerInitialized,
    // Export new batch actions
    upsertLocations,
    updateLocations,
    removeLocationsById
} = locationsSlice.actions;
export default locationsSlice.reducer;

// --- Selectors ---
// Export adapter selectors
export const {
    selectAll: selectAllLocations,
    selectById: selectLocationById,
    selectIds: selectLocationIds,
    selectEntities: selectLocationEntities,
    selectTotal: selectTotalLocations,
} = locationsAdapter.getSelectors((state: RootState) => state.locations);

// Base state selectors
export const selectLocationsLoading = (state: RootState) => state.locations.loading;
export const selectLocationsError = (state: RootState) => state.locations.error;
export const selectLocationsListenerActive = (state: RootState) => state.locations.listenerActive; 